import React, { useState, useMemo } from "react";
import ProductItem from "../../../../widgets/ProductItem";
import {
  Autocomplete,
  Box,
  Card,
  Grid,
  Pagination,
  TextField,
  Typography,
  Chip,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PageHeader from "../../common/PageHeader";
import CustomCircularP from "../../../../widgets/CustomCircularP";
import FilterDialogMui from "../../../../widgets/FilterDialogMui";
import AlphabetSortWidget from "./AlphabetSortWidget";
import useProductCategories from "../../visitor/registration/hooks/useProductCategories";
import useApiEp from "../../../../hooks/useApiEp";

const Products = () => {
  // --- States ---
  const [page, setPage] = useState(1);
  const [sq, setSq] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");

  // Dialog & Dropdown Filters
  const [selectedHall, setSelectedHall] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [selectedExhibitor, setSelectedExhibitor] = useState(null);

  // Category Filter (Multiselect)
  const [selectedCategories, setSelectedCategories] = useState([]);

  // --- API Fetch ---
  const { data: Mdata, isLoading: loadingCategories } = useProductCategories();
  const { data: exhibitorsData } = useApiEp(`/user/get-exhibitors`);
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-explore-products?page=${page}&sq=${sq}&hall=${selectedHall}&country=${selectedCountry}&letter=${selectedLetter}&cat=${selectedCategories.join(
      ","
    )}&exhibitor=${encodeURIComponent(selectedExhibitor || "")}`
  );

  // Extract Category (A01.00-Turning Machines, etc.) for the categories filter
  const uniqueCategories = useMemo(() => {
    if (!Mdata?.categories) return [];
    return [
      ...new Set(
        Mdata.categories
          .map((item) => item.Category || item.MainCategory || item.SubCategory)
          .filter(Boolean)
      ),
    ].sort();
  }, [Mdata]);

  // Dynamic Exhibitor Options List
  const exhibitorOptions = useMemo(() => {
    const set = new Set();

    // From exhibitors API
    if (exhibitorsData?.data && Array.isArray(exhibitorsData.data)) {
      exhibitorsData.data.forEach((ex) => {
        const name =
          ex?.companyShortName?.value ||
          ex?.company_name ||
          ex?.name ||
          ex?.username;
        if (name) set.add(name);
      });
    }

    // From loaded products list as fallback
    if (data?.data && Array.isArray(data.data)) {
      data.data.forEach((p) => {
        const name =
          p?.exhibitor?.companyShortName?.value ||
          p?.exhibitor?.company_name ||
          p?.exhibitor?.name ||
          p?.exhibitor_name ||
          p?.company_name;
        if (name) set.add(name);
      });
    }

    return Array.from(set).sort();
  }, [exhibitorsData, data]);

  // Client side filtering & sorting
  const sortedProducts = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return [];
    let list = [...data.data];

    // Filter by selected Exhibitor
    if (selectedExhibitor) {
      list = list.filter((p) => {
        const exName =
          p?.exhibitor?.companyShortName?.value ||
          p?.exhibitor?.company_name ||
          p?.exhibitor?.name ||
          p?.exhibitor_name ||
          p?.company_name ||
          "";
        return exName.toLowerCase() === selectedExhibitor.toLowerCase();
      });
    }

    return list.sort((a, b) => {
      const nameA = (a.title || a.name || a.product_name || "")
        .toString()
        .trim()
        .toLowerCase();
      const nameB = (b.title || b.name || b.product_name || "")
        .toString()
        .trim()
        .toLowerCase();

      if (!nameA) return 1;
      if (!nameB) return -1;

      return sortOrder === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [data, sortOrder, selectedExhibitor]);

  // --- Handlers ---
  const onItemSearch = (e) => {
    setSq(e.target.value);
    setPage(1);
  };

  const handleCategoriesChange = (event, newValue) => {
    setSelectedCategories(newValue);
    setPage(1);
  };

  const handleLetterSelect = (newV) => {
    setSelectedLetter(newV || "");
    setPage(1);
  };

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const onPageChange = (e, newPage) => {
    setPage(newPage);
  };

  // --- Product Handlers ---
  const onEditProduct = () => {};
  const onDeleteProduct = () => {};
  const onSelectProduct = (product) => {};

  // --- Helpers ---
  const totalPages = data?.totalItems ? Math.ceil(data.totalItems / 30) : 0;
  const hasProducts = sortedProducts && sortedProducts.length > 0;

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <CustomCircularP show={isLoading || loadingCategories} />

      <FilterDialogMui
        selectedCountry={selectedCountry}
        setSelectedCountry={(val) => {
          setSelectedCountry(val);
          setPage(1);
        }}
        selectedHall={selectedHall}
        setSelectedHall={(val) => {
          setSelectedHall(val);
          setPage(1);
        }}
        selectedLetter={selectedLetter}
        setSelectedLetter={(val) => {
          setSelectedLetter(val);
          setPage(1);
        }}
        open={openFilter}
        setOpen={setOpenFilter}
      />

      <Box sx={{ mb: 4 }}>
        <PageHeader
          title="Products & Solutions"
          subtitle="Explore products, machinery, and equipment showcase"
          icon={Inventory2Icon}
          count={data?.totalItems}
          countLabel="Items"
        />

        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eef2f6",
            boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
            p: 3,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Exhibitor Filter (Left) */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="medium"
                options={exhibitorOptions}
                value={selectedExhibitor}
                onChange={(event, newValue) => {
                  setSelectedExhibitor(newValue);
                  setPage(1);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Exhibitor"
                    placeholder="Filter by Exhibitor..."
                  />
                )}
              />
            </Grid>

            {/* Dynamic Categories (Middle) */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                multiple
                size="medium"
                options={uniqueCategories}
                value={selectedCategories}
                onChange={handleCategoriesChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categories"
                    placeholder="Select..."
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      color="primary"
                      variant="outlined"
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
              />
            </Grid>

            {/* Search Input (Right) */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search Products by name or keyword..."
                variant="outlined"
                value={sq}
                onChange={onItemSearch}
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f8fafc",
                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#0288d1" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Card>
      </Box>

      <Box sx={{ mb: 3, width: "100%" }}>
        <AlphabetSortWidget
          selectedLetter={selectedLetter}
          onSelectLetter={handleLetterSelect}
          sortOrder={sortOrder}
          onSortToggle={handleSortToggle}
        />
      </Box>

      {/* Product List Content */}
      {!isLoading && !hasProducts ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="30vh"
        >
          <Typography variant="h6" color="textSecondary">
            No products found matching your criteria.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {sortedProducts.map((item, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={item.id || index}
              sx={{ display: "flex" }}
            >
              <ProductItem
                viewMode={true}
                onSelectProduct={onSelectProduct}
                onEditProduct={onEditProduct}
                onDeleteProduct={onDeleteProduct}
                product={item}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box mt={4} mb={2} justifyContent="center" display="flex">
          <Pagination
            count={totalPages}
            page={page}
            onChange={onPageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default Products;
